/**
 * Georgi's locomotion state machine — pure TypeScript, no Phaser import, unit-tested in Vitest.
 *
 * States: idle | walk | run | air, with facing left | right. Each tick the scene feeds one
 * InputFrame + EnvFrame and gets back the state, the pose to draw (action + frame + flip), the
 * horizontal motion target for Arcade, and the effects to apply (takeoff velocity, facing flip,
 * landing). All decisions live here; the scene only executes them.
 *
 * Playback model
 * - A state's loop plays by wall-clock at the tuned cycle rate, always from frame 0 after a clip.
 * - Every state change plays the manifest's transition clip(s) for that edge as a queue
 *   (idle→run = idle-to-walk, walk-to-run). Grounded clips advance by time over their tuned
 *   duration; the takeoff clip by the ascent phase (1 − |vy| / vy0); the descent clips by the
 *   fall phase (vy / vy0), so physics owns Y and the frames follow it.
 * - Turns (walk-turn / run-turn) play on reversed input, are only interrupted by a jump or by
 *   leaving the ground, and flip the facing exactly when their last frame completes: that frame
 *   is the mirrored destination loop's frame 0, which then starts at frame 0.
 * - Other one-shots can be interrupted by state-changing input: the interruption continues from
 *   the clip's "visual" state (its from-state before clipMidpoint, its to-state after), so an
 *   early-interrupted brake simply resumes the walk loop and never traps the machine.
 * - Jumps are edge-triggered (one takeoff per press); grounded requires non-upward velocity;
 *   coyote time only after walking off a ledge, never after a jump; a jump buffer fires a press
 *   made shortly before landing on the landing tick.
 *
 * N1 coverage gaps and the policy taken (see README "Limitations"):
 * - no jump from idle/walk → run-to-jump entered at its first lifted frame (pose pop accepted);
 * - no fall → walking off a ledge is `air` with the descent clip from its apex frame;
 * - no hard landing → a descent clip cut short by an early landing finishes by time on the ground;
 * - no standing turn → an idle reversal is an instant flip;
 * - no climb → not in this room.
 */
import type { N1ActionId } from '../assets/n1Manifest';
import type { ActionGraph, GroundState, LocomotionState } from './actionGraph';
import {
  ascentPhase,
  descentPhase,
  loopFramePhase,
  phaseToFramePhase,
  type FramePhase,
} from './playback';
import type { PlayerTuning, TransitionActionId } from './tuning';

export type Facing = 'left' | 'right';
export type MoveAxis = -1 | 0 | 1;

export interface InputFrame {
  moveAxis: MoveAxis;
  runHeld: boolean;
  /** True only on the tick a jump key went down (edge-triggered, auto-repeat filtered). */
  jumpPressed: boolean;
}

export interface EnvFrame {
  /** Standing on a surface per physics (the machine additionally requires vy >= 0). */
  grounded: boolean;
  /** Vertical velocity, Phaser sign (+y down). */
  vy: number;
  dtMs: number;
  /** Extra mid-air jumps available right now (power-up); default 0. */
  airJumps?: number;
  /** Multiplier on walk/run speed targets (power-up); default 1. */
  speedScale?: number;
  /**
   * Actual horizontal velocity (px/s). Walk/run loops advance with the distance travelled so the
   * feet stay planted; omitted = nominal speed (wall-clock at the tuned cycle).
   */
  vx?: number;
}

export interface Pose {
  action: N1ActionId;
  frame: number;
  /** Right facing is a runtime mirror: there are no right-facing frames. */
  flipX: boolean;
  /** Phase of the current transition clip, null while a loop plays. */
  phase: number | null;
  /** Clips queued after the current one. */
  queue: N1ActionId[];
  /** Runtime in-betweening: the frame to draw over this one and how much of it (0..1). */
  blend: { nextFrame: number; fraction: number };
}

export type Effect =
  | { kind: 'jump'; velocityY: number; airborne: boolean }
  | { kind: 'flip'; facing: Facing }
  | { kind: 'land'; state: GroundState; impactVy: number };

export interface Motion {
  /** Horizontal velocity to approach (px/s). */
  targetVx: number;
  /** px/s² to approach it with; 0 keeps the current velocity (airborne momentum). */
  acceleration: number;
}

export interface StepResult {
  state: LocomotionState;
  facing: Facing;
  pose: Pose;
  motion: Motion;
  effects: Effect[];
}

type ClipDriver = 'time' | 'ascent' | 'descent';

interface Clip {
  action: N1ActionId;
  startFrame: number;
  driver: ClipDriver;
  elapsedMs: number;
  phase: number;
}

const axisFacing = (axis: MoveAxis): Facing | null =>
  axis === 0 ? null : axis < 0 ? 'left' : 'right';
const facingDir = (facing: Facing): 1 | -1 => (facing === 'right' ? 1 : -1);
const opposite = (facing: Facing): Facing => (facing === 'left' ? 'right' : 'left');

export class LocomotionMachine {
  private st: LocomotionState = 'idle';
  private face: Facing;
  private clip: Clip | null = null;
  private queue: N1ActionId[] = [];
  private loopMs = 0;
  private coyoteMs = 0;
  private bufferMs = 0;
  /** Took off since last standing (no coyote jump after a real jump). */
  private jumped = false;
  /** Ground state we last stood in (picks the takeoff entry frame and coyote jumps). */
  private lastGround: GroundState = 'idle';
  private prevVy = 0;
  private speedScale = 1;
  private lastPose: Pose;

  constructor(
    private readonly graph: ActionGraph,
    private readonly tuning: PlayerTuning,
    facing: Facing = 'right',
  ) {
    this.face = facing;
    this.lastPose = this.composePose(0);
  }

  get state(): LocomotionState {
    return this.st;
  }

  get facing(): Facing {
    return this.face;
  }

  get pose(): Pose {
    return this.lastPose;
  }

  /** Coyote window left (ms), for the overlay. */
  get coyoteLeftMs(): number {
    return this.coyoteMs;
  }

  /** Jump buffer left (ms), for the overlay. */
  get jumpBufferLeftMs(): number {
    return this.bufferMs;
  }

  /** Deterministic reset (respawn): idle loop from frame 0, timers cleared. */
  reset(facing: Facing): void {
    this.st = 'idle';
    this.face = facing;
    this.clip = null;
    this.queue = [];
    this.loopMs = 0;
    this.coyoteMs = 0;
    this.bufferMs = 0;
    this.jumped = false;
    this.lastGround = 'idle';
    this.prevVy = 0;
    this.lastPose = this.composePose(0);
  }

  update(input: InputFrame, env: EnvFrame): StepResult {
    const effects: Effect[] = [];
    const dt = Math.max(0, env.dtMs);
    const grounded = env.grounded && env.vy >= 0;
    let poseVy = env.vy;

    this.coyoteMs = Math.max(0, this.coyoteMs - dt);
    this.bufferMs = Math.max(0, this.bufferMs - dt);
    if (this.clip === null) this.loopMs += dt * this.loopRate(env);
    this.advanceTimedClip(dt, effects);

    if (this.st !== 'air' && !grounded) this.enterFall();
    if (this.st === 'air' && grounded) this.land(input, effects);

    this.speedScale = env.speedScale ?? 1;
    let motion: Motion;
    if (this.st === 'air') {
      if (input.jumpPressed) {
        if (this.coyoteMs > 0 && !this.jumped) {
          this.takeoff(effects);
          poseVy = -this.tuning.jumpVelocity;
        } else if ((env.airJumps ?? 0) > 0) {
          // Power-up double jump: re-enters the takeoff clip at its lifted frame, mid-air.
          this.takeoff(effects, true);
          poseVy = -this.tuning.jumpVelocity;
        } else this.bufferMs = this.tuning.jumpBufferMs;
      }
      const wanted = axisFacing(input.moveAxis);
      if (wanted && wanted !== this.face) {
        this.face = wanted;
        effects.push({ kind: 'flip', facing: wanted });
      }
      this.airClip(input, poseVy);
      motion = this.airMotion(input);
    } else if (input.jumpPressed) {
      this.lastGround = this.st as GroundState;
      this.takeoff(effects);
      poseVy = -this.tuning.jumpVelocity;
      this.airClip(input, poseVy);
      motion = this.airMotion(input);
    } else {
      motion = this.groundStep(input, effects);
    }

    this.prevVy = env.vy;
    this.lastPose = this.composePose(poseVy);
    return { state: this.st, facing: this.face, pose: this.lastPose, motion, effects };
  }

  /**
   * Stride sync: walk/run loops advance with |vx| relative to the loop's nominal speed
   * (stride / cycle), clamped so the pose never freezes when blocked nor spins when boosted.
   */
  private loopRate(env: EnvFrame): number {
    if (env.vx === undefined || this.st === 'air' || this.st === 'idle') return 1;
    const t = this.tuning;
    const loop = this.graph.loops[this.st];
    if (loop !== 'walk-loop' && loop !== 'run-loop') return 1;
    const nominal = (t.loopStridePx[loop] / t.loopCycleMs[loop]) * 1000;
    if (nominal <= 0) return 1;
    const rate = Math.abs(env.vx) / nominal;
    return Math.min(t.loopRate.max, Math.max(t.loopRate.min, rate));
  }

  // ------------------------------------------------------------------ clips

  private durationMs(action: N1ActionId): number {
    return this.tuning.transitionMs[action as TransitionActionId] ?? 1;
  }

  private startClip(action: N1ActionId, driver: ClipDriver, startFrame = 0, phase = 0): Clip {
    const clip: Clip = {
      action,
      startFrame,
      driver,
      elapsedMs: phase * this.durationMs(action),
      phase,
    };
    this.clip = clip;
    return clip;
  }

  /** Advances a time-driven clip; on completion flips (turns), pops the queue or resumes the loop. */
  private advanceTimedClip(dt: number, effects: Effect[]): void {
    const clip = this.clip;
    if (!clip || clip.driver !== 'time') return;
    clip.elapsedMs += dt;
    clip.phase = clip.elapsedMs / this.durationMs(clip.action);
    if (clip.phase < 1) return;
    this.completeClip(effects);
  }

  private completeClip(effects: Effect[]): void {
    const clip = this.clip;
    if (!clip) return;
    if (this.graph.actions[clip.action].facingChange) {
      this.face = opposite(this.face);
      effects.push({ kind: 'flip', facing: this.face });
    }
    const next = this.queue.shift();
    if (next) this.startClip(next, 'time');
    else {
      this.clip = null;
      this.loopMs = 0;
    }
  }

  private isTurning(): boolean {
    return this.clip !== null && this.graph.actions[this.clip.action].facingChange;
  }

  /** The state the current pose is nearest to (decides where an interruption continues from). */
  private visualState(): GroundState {
    const clip = this.clip;
    const state = this.st as GroundState;
    if (!clip || clip.driver !== 'time') return state;
    const info = this.graph.actions[clip.action];
    if (info.facingChange || info.kind === 'loop') return state;
    if (info.from === 'air') return info.to as GroundState;
    return (clip.phase < this.tuning.clipMidpoint ? info.from : info.to) as GroundState;
  }

  // ----------------------------------------------------------------- ground

  private desiredState(input: InputFrame): GroundState {
    if (input.moveAxis === 0) return 'idle';
    return input.runHeld ? 'run' : 'walk';
  }

  private groundStep(input: InputFrame, effects: Effect[]): Motion {
    if (this.isTurning()) return this.turnMotion();

    const wanted = axisFacing(input.moveAxis);
    if (wanted && wanted !== this.face) {
      // Reversal: the turn of the state the pose is in — still walk/run during an early brake
      // (a key rollover from → to ← braked for a frame), so it plays the turn, not a flip.
      const visual = this.visualState();
      const turn = visual === 'idle' ? undefined : this.graph.turns[visual];
      if (turn) {
        this.st = visual;
        this.lastGround = visual;
        this.startClip(turn, 'time');
        this.queue = [];
        return this.turnMotion();
      }
      // No standing turn in N1: an idle reversal is an instant flip.
      this.face = wanted;
      effects.push({ kind: 'flip', facing: wanted });
    }

    const desired = this.desiredState(input);
    if (desired !== this.st) this.changeState(desired);
    return this.groundMotion(desired, input);
  }

  private changeState(to: GroundState): void {
    const from = this.visualState();
    this.st = to;
    this.lastGround = to;
    const chain = this.graph.path(from, to);
    const first = chain[0];
    if (!first) {
      // Back to the state the pose already shows: resume its loop.
      this.clip = null;
      this.queue = [];
      this.loopMs = 0;
      return;
    }
    if (this.clip && this.clip.driver === 'time' && this.clip.action === first) {
      // Already playing the first clip of the chain: keep its progress.
      this.queue = chain.slice(1);
      return;
    }
    this.startClip(first, 'time');
    this.queue = chain.slice(1);
  }

  private groundMotion(state: GroundState, input: InputFrame): Motion {
    const t = this.tuning;
    const k = this.speedScale;
    switch (state) {
      case 'idle':
        return { targetVx: 0, acceleration: t.brakeDeceleration };
      case 'walk':
        return { targetVx: input.moveAxis * t.walkSpeed * k, acceleration: t.groundAcceleration };
      case 'run':
        return { targetVx: input.moveAxis * t.runSpeed * k, acceleration: t.groundAcceleration };
    }
  }

  /** Turn: bleed off until the pivot, then step out the other way at the state's speed. */
  private turnMotion(): Motion {
    const t = this.tuning;
    const clip = this.clip;
    const progress = clip ? clip.phase : 1;
    if (progress < t.turnPivot) return { targetVx: 0, acceleration: t.brakeDeceleration };
    const speed = (this.st === 'run' ? t.runSpeed : t.walkSpeed) * this.speedScale;
    return { targetVx: -facingDir(this.face) * speed, acceleration: t.groundAcceleration };
  }

  // -------------------------------------------------------------------- air

  private enterFall(): void {
    this.lastGround = this.st as GroundState;
    this.st = 'air';
    this.coyoteMs = this.jumped ? 0 : this.tuning.coyoteTimeMs;
    this.clip = null;
    this.queue = [];
  }

  private takeoff(effects: Effect[], airborne = false): void {
    const info = this.graph.actions[this.graph.takeoff];
    const startFrame = this.lastGround === 'run' && !airborne ? 0 : info.firstLiftedFrame;
    this.st = 'air';
    this.jumped = true;
    this.coyoteMs = 0;
    this.bufferMs = 0;
    this.queue = [];
    this.startClip(this.graph.takeoff, 'ascent', startFrame);
    effects.push({ kind: 'jump', velocityY: -this.tuning.jumpVelocity, airborne });
  }

  private descentTarget(input: InputFrame): 'run' | 'idle' {
    return input.runHeld && input.moveAxis !== 0 ? 'run' : 'idle';
  }

  /** Keeps the airborne clip in step with physics: ascent while rising, descent from the apex. */
  private airClip(input: InputFrame, vy: number): void {
    const t = this.tuning;
    if (vy < 0) {
      const clip =
        this.clip && this.clip.driver === 'ascent'
          ? this.clip
          : this.startClip(this.graph.takeoff, 'ascent');
      clip.phase = Math.max(clip.phase, ascentPhase(vy, t.jumpVelocity));
      return;
    }
    const action = this.graph.descent(this.descentTarget(input));
    const clip =
      this.clip && this.clip.driver === 'descent' ? this.clip : this.startClip(action, 'descent');
    clip.action = action; // the landing target may change mid-fall; the fall phase is kept
    clip.phase = Math.max(clip.phase, descentPhase(vy, t.jumpVelocity));
  }

  private airMotion(input: InputFrame): Motion {
    const t = this.tuning;
    if (input.moveAxis === 0) return { targetVx: 0, acceleration: 0 };
    const speed = (input.runHeld ? t.runSpeed : t.walkSpeed) * this.speedScale;
    return { targetVx: input.moveAxis * speed, acceleration: t.airAcceleration };
  }

  /** Landing tick: buffered jump re-takes off, otherwise finish the descent clip on the ground. */
  private land(input: InputFrame, effects: Effect[]): void {
    const impactVy = this.prevVy;
    this.jumped = false;
    this.coyoteMs = 0;
    if (this.bufferMs > 0) {
      this.lastGround = this.desiredState(input);
      this.takeoff(effects);
      return;
    }
    const to = this.desiredState(input);
    this.st = to;
    this.lastGround = to;
    effects.push({ kind: 'land', state: to, impactVy });
    const clip = this.clip;
    if (clip && clip.driver === 'descent') {
      const info = this.graph.actions[clip.action];
      const chain = this.graph.path(info.to as GroundState, to);
      // Remainder (early landing on a platform above takeoff) plays by time; a full arc is done.
      const remainder = this.startClip(clip.action, 'time', 0, clip.phase);
      this.queue = chain;
      if (remainder.phase >= 1) this.completeClip(effects);
    } else {
      this.clip = null;
      this.queue = [];
      this.loopMs = 0;
    }
  }

  // ------------------------------------------------------------------- pose

  private composePose(vy: number): Pose {
    const clip = this.clip;
    const flipX = this.face === 'right';
    if (clip) {
      const info = this.graph.actions[clip.action];
      const phase =
        clip.driver === 'time'
          ? clip.phase
          : clip.driver === 'ascent'
            ? Math.max(clip.phase, ascentPhase(vy, this.tuning.jumpVelocity))
            : Math.max(clip.phase, descentPhase(vy, this.tuning.jumpVelocity));
      const fp: FramePhase = phaseToFramePhase(phase, info.frames, clip.startFrame);
      return {
        action: clip.action,
        frame: fp.frame,
        flipX,
        phase,
        queue: [...this.queue],
        blend: { nextFrame: fp.next, fraction: fp.fraction },
      };
    }
    const state: GroundState = this.st === 'air' ? 'idle' : this.st;
    const loop = this.graph.loops[state];
    const info = this.graph.actions[loop];
    const fp = loopFramePhase(this.loopMs, this.tuning.loopCycleMs[loop], info.frames);
    return {
      action: loop,
      frame: fp.frame,
      flipX,
      phase: null,
      queue: [],
      blend: { nextFrame: fp.next, fraction: fp.fraction },
    };
  }
}
